import { useState, useEffect, useRef } from "react"
import { Box, TextField, InputAdornment, CircularProgress } from "@mui/material"
import { getSession } from "../../../../auth"

import SearchIcon from "@mui/icons-material/Search"
import useNotification from "../../../Notification"
import api from "../../../../api"
import dayjs from "dayjs"

import "./styles.css"

export default function SearchArea({ chat }) {
    const [query, setQuery] = useState("")
    const [debouncedQuery, setDebouncedQuery] = useState("")
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedResultId, setSelectedResultId] = useState(null)

    const abortRef = useRef(null)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query)
        }, 1000)

        return () => clearTimeout(handler)
    }, [query])

    useEffect(() => {
        if (!debouncedQuery.trim()) {
            setResults([])
            return
        }

        if (abortRef.current) {
            abortRef.current.abort()
        }

        const controller = new AbortController()
        abortRef.current = controller

        const fetchResults = async () => {
            try {
                setLoading(true)
                const enterpriseId = getSession().profile.enterprise.id
                const params = {
                    query: debouncedQuery,
                    ...(chat.contact?.id
                        ? { contactId: chat.contact.id }
                        : { channel: `${chat.platform}:${chat.callerid}` }
                    )
                }
                const response = await api.get(
                    `api/enterprises/${enterpriseId}/contacts/search_messages/`,
                    {
                        params: params,
                        signal: controller.signal,
                    }
                )
                if (response.status === 200) {
                    if (getSession().profile?.enterprise?.settings?.all_queue_administrator) {
                        setResults(response.data.hits)
                    } else {
                        const queues = getSession().profile?.queues?.map((queue) => String(queue.id)) ?? []
                        const filteredHits = response.data.hits.filter((hit) => {
                            return hit.queue_id === "0" || hit.queue_id == null || queues.includes(hit.queue_id)
                        })
                        setResults(filteredHits)   
                    }
                }
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Search failed:", error)
                }
            } finally {
                setLoading(false)
            }
        }

        fetchResults()

        return () => controller.abort()
    }, [debouncedQuery])

    const normalize = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")

    const highlightText = (text) => {
        if (!query)
            return text

        const normalizedText = normalize(text)
        const normalizedQuery = normalize(query)

        const regex = new RegExp(`(${normalizedQuery})`, "gi")

        const parts = []
        
        let lastIndex = 0

        normalizedText.replace(regex, (match, _, index) => {
            parts.push(text.slice(lastIndex, index))
            parts.push(
                <strong key={index}>
                    {text.slice(index, index + match.length)}
                </strong>
            )
            lastIndex = index + match.length
        })

        parts.push(text.slice(lastIndex))

        return parts
    }

    const handleClick = async (item) => {
        setSelectedResultId(item.id)
        const queues = getSession().profile?.queues?.map((queue) => String(queue.id)) ?? []
        const response = await api.get(`api/LHC/chats/messages/jump/${item.chat_id}/${item.id}/`, { params: { queues }})
        if (response.status === 200) {
            if ("updateChatMessages" in window) {
                window.updateChatMessages(response.data)
                setTimeout(() => {
                    const element = document.getElementById(`msg-${item.id}`)
                    if (element) {
                        element.scrollIntoView({
                            behavior: "smooth",
                            block: "center"
                        })
                        // Highlighting selected message
                        const originalBg = element.style.backgroundColor
                        element.style.backgroundColor = "rgba(255, 230, 150, 0.8)"
                        element.style.transition = "background-color 0.5s ease"
                        setTimeout(() => {
                            element.style.backgroundColor = originalBg
                        }, 5000)
                    }
                }, 100)
            }
        }
        else if (response.status === 403) {
            useNotification(
                "Ops!",
                "Você não tem permissão para visualizar essa conversa.",
                "warning"
            )
        }
    }

    return (
        <Box className="search-area-container">
            <TextField
                size="small"
                fullWidth
                placeholder="Digite a mensagem que deseja procurar..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />
            {loading && (
                <Box padding="2.5rem" textAlign="center">
                    <CircularProgress size={20} />
                </Box>
            )}
            {!loading && (
                <Box className="search-results">
                    {results.map((item) => (
                        <Box
                            key={item.id}
                            onClick={() => handleClick(item)}
                            className={selectedResultId === item.id ? "search-result-item selected" : "search-result-item"}
                        >
                            <Box className="datetime">
                                {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                            </Box>
                            <Box className="content">
                                {highlightText(item.content)}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
            {!loading && debouncedQuery.trim() && results.length === 0 && (
                <Box className="no-results">
                    Nenhuma mensagem encontrada para
                    <strong> “{debouncedQuery}”</strong>
                </Box>
            )}
        </Box>
    )
}
